/**
 * Screen Code Mapper
 *
 * Maps legacy step numbers to screen codes for backward compatibility and migration.
 * This allows gradual migration from step-based to screen-code-based data structure.
 */

/**
 * Map of step numbers to screen codes (legacy compatibility)
 * Note: Some steps vary by flow (Medical, Cancellation, Baggage)
 */
export const STEP_TO_SCREEN_CODE: Record<number, string> = {
  1: '00-02-AVIS',              // Legal Notice
  2: '01-00-DEMANDEUR',         // Contact Preferences
  3: '01-01-CONTRAT',           // Contract Selection
  4: '01-02-AUTRES-CONTRATS',   // Other Insurance
  5: '02M-01-BENEFICIAIRE',     // Beneficiary (Medical flow)
  6: '02M-02-VOYAGE',           // Travel Confirmation (Medical)
  7: '02-00-GARANTIE',          // Claim Type Selection
  8: '02M-03-SPECIFIC',         // Specific Claim Type (Medical)
  9: '03M-00-ACCIDENT',         // Specific Verifications (Medical)
  10: '03M-01-DETAILS',         // Event Details (Medical)
  11: '03M-02-VERIFICATIONS',   // Medical Verification
  12: '04M-00-REMBOURSEMENT',   // Reimbursement Details (Medical)
  13: '04-01-FACTURES',         // Invoice Upload
  14: '04-02-VALIDATION',       // Expense Validation
  15: '04-03-JUSTIFICATIFS',    // Supporting Documents
  16: '05-00-ELIGIBILITE',      // Eligibility Analysis
  17: '05-01-PAIEMENT',         // Banking Info
  18: '05M-02-MANDAT',          // Mandate Signature (Medical)
  19: '05-03-AUTORISATIONS',    // Authorizations
  20: '05-03-FIN'               // Confirmation
};

/**
 * Reverse mapping: screen code to step number (for UI progression)
 */
export const SCREEN_CODE_TO_STEP: Record<string, number> = Object.entries(
  STEP_TO_SCREEN_CODE
).reduce((acc, [step, code]) => {
  acc[code] = Number(step);
  return acc;
}, {} as Record<string, number>);

/**
 * Get screen code for a given step number
 */
export function getScreenCodeForStep(step: number): string | null {
  return STEP_TO_SCREEN_CODE[step] || null;
}

/**
 * Get step number for a given screen code (for UI progression tracking)
 */
export function getStepForScreenCode(screenCode: string): number | null {
  return SCREEN_CODE_TO_STEP[screenCode] || null;
}

/**
 * Migrate legacy step-based data to screen-code-based data
 *
 * @param stepData Legacy stepData with numeric keys
 * @returns New stepData with screen code keys
 */
export function migrateStepDataToScreenCodes(
  stepData: Record<number, any>
): Record<string, any> {
  const migratedData: Record<string, any> = {};

  for (const [stepStr, data] of Object.entries(stepData)) {
    const step = Number(stepStr);
    const screenCode = STEP_TO_SCREEN_CODE[step];

    if (screenCode) {
      migratedData[screenCode] = data;
    }
  }

  return migratedData;
}

/**
 * Check if stepData uses legacy format (numeric keys)
 */
export function isLegacyStepData(stepData: Record<string | number, any>): boolean {
  const keys = Object.keys(stepData);
  if (keys.length === 0) return false;

  // Check if any key is a number (legacy format)
  return keys.some(key => !isNaN(Number(key)));
}

/**
 * Migrate stepData if needed (auto-detect legacy format)
 */
export function autoMigrateStepData(
  stepData: Record<string | number, any>
): Record<string, any> {
  if (!stepData || Object.keys(stepData).length === 0) {
    return {};
  }

  if (isLegacyStepData(stepData)) {
    return migrateStepDataToScreenCodes(stepData as Record<number, any>);
  }

  return stepData as Record<string, any>;
}

/**
 * Get screen code from current route path
 * Example: /claim/01-00-DEMANDEUR → 01-00-DEMANDEUR
 */
export function extractScreenCodeFromPath(path: string): string | null {
  const match = path.match(/\/claim\/([A-Z0-9\-]+)/);
  return match ? match[1] : null;
}
